class OoCountdownTimer extends HTMLElement {
  constructor() {
    super();
    this.values = { days: null, hours: null, mins: null, secs: null };
    this.cards = {};
    this.intervalId = null;
    this.flipTimeouts = {};
    this.flipDuration = 700;
    this.reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  }

  connectedCallback() {
    this.units = [
      { key: 'days', label: this.dataset.days || 'days' },
      { key: 'hours', label: this.dataset.hours || 'hours' },
      { key: 'mins', label: this.dataset.mins || 'mins' },
      { key: 'secs', label: this.dataset.secs || 'secs' },
    ];
    this.showButtonWhenExpired = this.dataset.showButtonWhenExpired === 'true';
    this.countdownBtnWrap = this.closest('.banner__box')?.querySelector('.countdown-btn');
    this.countdownBtn = this.countdownBtnWrap?.querySelector('a');
    this.render();
    this.start();
  }

  disconnectedCallback() {
    if (this.intervalId) clearInterval(this.intervalId);
    Object.values(this.flipTimeouts).forEach((id) => clearTimeout(id));
  }

  parseDeadline() {
    const timeValue = (this.dataset.time || '00:00').trim();
    const isoValue = (this.dataset.deadline || '').trim();
    const timeMatch = timeValue.match(/^(\d{1,2}):(\d{2})$/);
    const hours = timeMatch ? Math.min(23, Number(timeMatch[1])) : 0;
    const minutes = timeMatch ? Math.min(59, Number(timeMatch[2])) : 0;

    if (isoValue && /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}/.test(isoValue)) {
      const parsed = new Date(isoValue);
      if (!Number.isNaN(parsed.getTime())) return parsed;
    }

    const dayAttr = Number(this.dataset.day);
    const monthAttr = Number(this.dataset.month);
    const yearAttr = Number(this.dataset.year);
    if (dayAttr && monthAttr && yearAttr && monthAttr <= 12 && dayAttr <= 31) {
      return new Date(yearAttr, monthAttr - 1, dayAttr, hours, minutes, 0, 0);
    }

    // Fallback for older text values: prefer DD-MM-YYYY, else MM-DD-YYYY
    const dateValue = (this.dataset.date || '').trim();
    if (!/^\d{1,2}-\d{1,2}-\d{4}$/.test(dateValue)) return null;

    const parts = dateValue.split('-').map(Number);
    const first = parts[0];
    const second = parts[1];
    const year = parts[2];
    let day = first;
    let month = second;
    if (first <= 12 && second > 12) {
      month = first;
      day = second;
    }

    if (!month || !day || !year || month > 12 || day > 31) return null;
    return new Date(year, month - 1, day, hours, minutes, 0, 0);
  }

  getTimeLeft(deadline) {
    const difference = deadline.getTime() - Date.now();
    if (difference <= 0) {
      return { days: 0, hours: 0, mins: 0, secs: 0, expired: true };
    }

    return {
      days: Math.floor(difference / (1000 * 60 * 60 * 24)),
      hours: Math.floor((difference / (1000 * 60 * 60)) % 24),
      mins: Math.floor((difference / 1000 / 60) % 60),
      secs: Math.floor((difference / 1000) % 60),
      expired: false,
    };
  }

  pad(value) {
    return value.toString().padStart(2, '0');
  }

  createHalf(modifier, value) {
    return `
      <div class="oo-flip-half oo-flip-half--${modifier}">
        <span class="oo-flip-half__inn">${value}</span>
      </div>
    `;
  }

  render() {
    this.innerHTML = '';
    this.classList.add('oo-flip-timer');

    this.units.forEach((unit) => {
      const unitEl = document.createElement('div');
      unitEl.className = 'oo-flip-unit';
      unitEl.dataset.unit = unit.key;

      unitEl.innerHTML = `
        <div class="oo-flip-card" aria-hidden="true">
          <div class="oo-flip-card__base">
            ${this.createHalf('top', '00')}
            ${this.createHalf('bottom', '00')}
          </div>
          <div class="oo-flip-card__hinge">
            ${this.createHalf('top', '00')}
            ${this.createHalf('bottom', '00')}
          </div>
        </div>
        <span class="oo-flip-label">${unit.label}</span>
      `;

      this.appendChild(unitEl);

      const base = unitEl.querySelector('.oo-flip-card__base');
      const hinge = unitEl.querySelector('.oo-flip-card__hinge');

      this.cards[unit.key] = {
        root: unitEl,
        card: unitEl.querySelector('.oo-flip-card'),
        baseTop: base.querySelector('.oo-flip-half--top .oo-flip-half__inn'),
        baseBottom: base.querySelector('.oo-flip-half--bottom .oo-flip-half__inn'),
        hingeTop: hinge.querySelector('.oo-flip-half--top .oo-flip-half__inn'),
        hingeBottom: hinge.querySelector('.oo-flip-half--bottom .oo-flip-half__inn'),
        hinge,
      };
    });
  }

  setFaces(card, topValue, bottomValue, hingeTopValue, hingeBottomValue) {
    card.baseTop.textContent = topValue;
    card.baseBottom.textContent = bottomValue;
    card.hingeTop.textContent = hingeTopValue;
    card.hingeBottom.textContent = hingeBottomValue;
  }

  finishFlip(key, next) {
    const card = this.cards[key];
    if (!card) return;

    this.setFaces(card, next, next, next, next);
    card.card.classList.remove('is-flipping');
    card.hinge.style.animation = 'none';
    void card.hinge.offsetWidth;
    card.hinge.style.animation = '';

    if (this.flipTimeouts[key]) {
      clearTimeout(this.flipTimeouts[key]);
      delete this.flipTimeouts[key];
    }
  }

  setCardValue(key, value, animate) {
    const card = this.cards[key];
    if (!card) return;

    const next = this.pad(value);
    const previous = this.values[key] === null ? next : this.pad(this.values[key]);

    if (previous === next && this.values[key] !== null) return;

    // Complete any in-progress flip before starting a new one.
    if (card.card.classList.contains('is-flipping')) {
      this.finishFlip(key, previous);
    }

    if (!animate || this.reduceMotion || this.values[key] === null) {
      this.finishFlip(key, next);
      this.values[key] = value;
      return;
    }

    // Base shows new top + old bottom while hinge flips old→new.
    this.setFaces(card, next, previous, previous, next);

    card.card.classList.remove('is-flipping');
    void card.card.offsetWidth;
    card.card.classList.add('is-flipping');

    if (this.flipTimeouts[key]) clearTimeout(this.flipTimeouts[key]);
    this.flipTimeouts[key] = setTimeout(() => {
      this.finishFlip(key, next);
    }, this.flipDuration);

    this.values[key] = value;
  }

  setButtonVisibility(expired) {
    if (!this.countdownBtnWrap) return;

    if (this.showButtonWhenExpired) {
      if (expired) {
        this.countdownBtnWrap.hidden = false;
        this.countdownBtnWrap.removeAttribute('aria-hidden');
        this.countdownBtnWrap.classList.add('is-visible');
        this.countdownBtn?.removeAttribute('aria-disabled');
      } else {
        this.countdownBtnWrap.hidden = true;
        this.countdownBtnWrap.setAttribute('aria-hidden', 'true');
        this.countdownBtnWrap.classList.remove('is-visible');
      }
      return;
    }

    this.countdownBtnWrap.hidden = false;
    this.countdownBtnWrap.removeAttribute('aria-hidden');
  }

  showExpired() {
    const message = (this.dataset.message || '').trim();
    if (message) {
      this.innerHTML = `<span class="timeout">${message}</span>`;
    } else if (this.cards && Object.keys(this.cards).length) {
      this.units.forEach((unit) => this.setCardValue(unit.key, 0, false));
    }
    this.setButtonVisibility(true);
  }

  tick(animate) {
    const deadline = this.parseDeadline();
    if (!deadline) {
      this.showExpired();
      return false;
    }

    const timeLeft = this.getTimeLeft(deadline);
    this.units.forEach((unit) => {
      this.setCardValue(unit.key, timeLeft[unit.key], animate);
    });

    if (timeLeft.expired) {
      this.showExpired();
      return false;
    }

    this.setButtonVisibility(false);
    return true;
  }

  start() {
    if (this.intervalId) clearInterval(this.intervalId);
    if (!this.tick(false)) return;
    this.intervalId = setInterval(() => {
      if (!this.tick(true) && this.intervalId) {
        clearInterval(this.intervalId);
        this.intervalId = null;
      }
    }, 1000);
  }
}

if (!customElements.get('oo-countdown-timer')) {
  customElements.define('oo-countdown-timer', OoCountdownTimer);
}
